import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UrlInput } from "@/components/ui/url-input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Brand } from "@/types/brand";

const formSchema = z.object({
  name: z.string().min(1, "Brand name is required"),
  url: z
    .string()
    .min(1, "Website URL is required")
    .regex(/^https:\/\//, {
      message: "URL must start with https://",
    })
    .url({
      message: "Please enter a valid website URL.",
    }),
});

type FormValues = z.infer<typeof formSchema>;

interface WebsiteAnalysisStepProps {
  data: Brand;
  onNext: (data: Partial<Brand>) => void;
  isAnalyzing: boolean;
}

export function WebsiteAnalysisStep({
  data,
  onNext,
  isAnalyzing,
}: WebsiteAnalysisStepProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: data.name,
      url: data.url,
    },
  });

  const onSubmit = (values: FormValues) => {
    onNext(values);
  };

  return (
    <Card className="w-full max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle>Welcome to Creator</CardTitle>
        <CardDescription>
          Let's start by analyzing your website to personalize your content
          strategy
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Brand Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your brand name"
                      {...field}
                      className="text-base"
                    />
                  </FormControl>
                  {/* <FormDescription>
                    We'll analyze your website to understand your brand and
                    content needs
                  </FormDescription> */}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Website URL</FormLabel>
                  <FormControl>
                    <UrlInput
                      placeholder="https://yourwebsite.com"
                      {...field}
                      className="text-base"
                    />
                  </FormControl>
                  <FormDescription>
                    Enter your website URL starting with https://. We'll analyze
                    it to understand your brand and content needs.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="space-y-4">
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isAnalyzing}
              >
                {isAnalyzing ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Analyzing Your Website...
                  </>
                ) : (
                  "Let AI Analyze Your Website"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-center text-sm text-muted-foreground">
        Your data is securely processed to create personalized content
        strategies
      </CardFooter>
    </Card>
  );
}
